import { Component } from '@theme/component';
import { morph } from '@theme/morph';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * A custom element that shows the size guide image for the selected variant.
 *
 * The variant picker already re-fetches the product page on every option change, but on a product
 * page it only morphs itself. This component reuses that same response (`detail.html`) and morphs
 * its own contents, so the image is always the one Liquid rendered for the newly selected variant.
 *
 * Mirrors the pattern used by `product-inventory.js`.
 *
 * @extends {Component}
 */
class VariantSizeGuide extends Component {
  connectedCallback() {
    super.connectedCallback();
    const closestSection = this.closest('.shopify-section, dialog');
    closestSection?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    const closestSection = this.closest('.shopify-section, dialog');
    closestSection?.removeEventListener(StandardEvents.productSelect, this.#handleProductSelect);
  }

  /**
   * Handles product select event by swapping in the newly selected variant's size guide.
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html, newProduct } = detail;

        if (newProduct) {
          this.dataset.productId = newProduct.id;
        } else if (detail.productId && detail.productId !== this.dataset.productId) {
          return;
        }

        // Prefer the matching block instance — a template may hold more than one size guide.
        const blockId = this.dataset.blockId;
        const newSizeGuide =
          (blockId && html.querySelector(`variant-size-guide[data-block-id="${CSS.escape(blockId)}"]`)) ||
          html.querySelector('variant-size-guide');

        if (!newSizeGuide) return;

        morph(this, newSizeGuide, { childrenOnly: true });
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[variant-size-guide] Event promise rejected:', error);
      });
  };
}

if (!customElements.get('variant-size-guide')) {
  customElements.define('variant-size-guide', VariantSizeGuide);
}
